


import CommonUtil from '../../util/CommonUtil';
// src/components/league/DraftComponent.tsx
// 이 파일은 리그 상태가 'draft'일 때 LeagueSessionPage에서 렌더링됩니다.
import React, { useState, useEffect, useCallback, useReducer, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext'; // 가정: 인증 컨텍스트가 존재
import { LeagueDetailDto, LeagueDraftEventLogDto, LeagueDraftPlayerResponse, LeagueDraftPollResponse, LeagueParticipantDto, LeagueSessionParticipantRankResponse } from '@playball/common/dto/League';
import { KBOLeagueDraftRuleDto, KBOLeaguePositionRuleDto, KBOLeagueRuleResponse } from '@playball/common/dto/KBO';
import { DataResponse } from '@playball/common/dto/Common';
import { logger } from '@sentry/browser';

// 백엔드의 draftPlayerList 응답과 일치하는 선수 정보 타입
interface Player {
    id: number;
    name: string;
    team: string;
    position: string;
    sum: number; // 스탯 합계 점수
    avg: number; // 평균 점수
    sH?: number;
    sHR?: number;
    sSO?: number;
    sIP?: number;
    sW?: number;
}

// 자신의 팀 로스터 슬롯 타입
interface RosterSlot extends KBOLeaguePositionRuleDto {
    player: Player | null; // 선택된 선수 정보
}

const useMediaQuery = (query: string): boolean => {
    const [matches, setMatches] = useState(window.matchMedia(query).matches);

    useEffect(() => {
        const mediaQueryList = window.matchMedia(query);
        const listener = (event: MediaQueryListEvent) => setMatches(event.matches);

        // addEventListener와 removeEventListener를 사용하여 최신 브라우저와 호환성을 맞춥니다.
        mediaQueryList.addEventListener('change', listener);
        return () => mediaQueryList.removeEventListener('change', listener);
    }, [query]);

    return matches;
};

// 드래프트 상태 변경 로직을 관리하는 리듀서


// 드래프트 전체 상태를 관리하는 객체 타입
interface DraftState {
    rule: KBOLeagueDraftRuleDto;
    teamSlotList: RosterSlot[];
    availablePlayerList: LeagueDraftPlayerResponse[];
    currentTurn: number;
    currentTurnUserCode: string | null;
    myUserCode: string;
    isMyTurn: boolean;
    isAutoPick: boolean;
    timerEndTime: number; // 타이머 종료 시점의 timestamp
    lastEventId: number;
    isDraftFinished: boolean;
}
type DraftAction =
    | { type: 'INITIALIZE';     payload: { rule: KBOLeagueDraftRuleDto, teamSlotList: RosterSlot[], availablePlayerList: LeagueDraftPlayerResponse[], myUserCode: string, draftAt: string } }
    | { type: 'START';          payload: { eventAt: string } }
    | { type: 'SET_TURN';       payload: { turn: number; userCode: string; eventAt: string } }
    | { type: 'PLAYER_PICKED';  payload: { pickedPlayerId: number; positionIndex: number; pickerUserCode: string; eventAt: string; } }
    | { type: 'SET_AUTO_PICK';  payload: boolean }
    | { type: 'DRAFT_FINISH';   payload: void };
function draftReducer(state: DraftState, action: DraftAction): DraftState {
    const { type, payload } = action;
    switch (type) {
        case 'INITIALIZE':
        {
            const duration = payload.rule.ready_timer * 1000;
            return { 
                ...state,
                rule: payload.rule,
                teamSlotList: payload.teamSlotList,
                availablePlayerList: payload.availablePlayerList,
                myUserCode: payload.myUserCode,
                timerEndTime: new Date(payload.draftAt).getTime() + duration,
            };
        }
        case 'START':
        {
            const duration = state.rule.next_turn_delay_timer * 1000;
            return {
                ...state,
                timerEndTime: new Date(payload.eventAt).getTime() + duration,
            }
        }
        case 'SET_TURN':
            const { turn, userCode, eventAt } = payload;
            const duration = state.rule.pick_timer * 1000;
            return {
                ...state,
                currentTurn: turn,
                currentTurnUserCode: userCode,
                isMyTurn: userCode === state.myUserCode,
                timerEndTime: new Date(eventAt).getTime() + duration,
            };
        case 'PLAYER_PICKED': {
            const { pickedPlayerId, positionIndex, pickerUserCode, eventAt } = payload;
            const duration = state.rule.next_turn_delay_timer * 1000;
            const updateState = {
                ...state,
                currentTurnUserCode: null,
                isMyTurn: false,
                timerEndTime: new Date(eventAt).getTime() + duration,
            };

            const playerIndex = state.availablePlayerList.findIndex(p => p.id === pickedPlayerId);
            if (playerIndex < 0) return updateState;

            const updateTeamSlotList = [...state.teamSlotList];
            let updateAvailablePlayerList = [...state.availablePlayerList];
            updateAvailablePlayerList.splice(playerIndex, 1);

            if (pickerUserCode === state.myUserCode) {
                // 슬롯 갱신
                updateTeamSlotList[positionIndex] = { ...updateTeamSlotList[positionIndex], player: state.availablePlayerList[playerIndex] };

                // 선택 가능 선수 갱신
                updateAvailablePlayerList = updateAvailablePlayerList
                    .filter(player => updateTeamSlotList
                        .some(slot => {
                            if (slot.player) {
                                return false;
                            }

                            return slot.position_list.length === 0 
                                || slot.position_list.includes(player.position);
                        }));
            }

            return {
                ...updateState,
                availablePlayerList: updateAvailablePlayerList,
                teamSlotList: updateTeamSlotList,
            };
        }
        case 'SET_AUTO_PICK':
            return { ...state, isAutoPick: payload };
        case 'DRAFT_FINISH':
             return { ...state, isDraftFinished: true };
        default:
            return state;
    }
}

// --- 하위 컴포넌트들 ---

const DraftTimer: React.FC<{ endTime: number }> = ({ endTime }) => {
    const [timeLeft, setTimeLeft] = useState(0);

    useEffect(() => {
        const updateTimer = () => {
            const remaining = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
            setTimeLeft(remaining);
        };
        updateTimer();
        const intervalId = setInterval(updateTimer, 1000);
        return () => clearInterval(intervalId);
    }, [endTime]);

    return <div className="timer-value">{String(timeLeft).padStart(2, '0')}</div>;
};

const PlayerModal: React.FC<{ player: LeagueDraftPlayerResponse; onClose: () => void }> = ({ player, onClose }) => {
  // 'Escape' 키를 눌러 모달을 닫는 이펙트
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h5 className="modal-title">{player.name} ({player.position} - {player.team})</h5>
          <button type="button" className="close-button" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <h6>Season Stats</h6>
          <p>평균 점수: {player.avg.toFixed(2)}</p>
          <p>합계 점수: {player.sum.toFixed(2)}</p>
          {player.position === 'P' ? (
            <div>
              <p>이닝: {player.sIP}</p>
              <p>승리: {player.sW}</p>
              <p>탈삼진: {player.sSO}</p>
            </div>
          ) : (
            <div>
              <p>안타: {player.sH}</p>
              <p>홈런: {player.sHR}</p>
              <p>탈삼진: {player.sSO}</p>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose}>닫기</button>
        </div>
      </div>
       <style>
        {`/* Modal Styles */
                .modal-backdrop { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background-color: rgba(255, 255, 255, 0.6); display: flex; justify-content: center; align-items: center; z-index: 1050; }
                .modal-content { background-color: white; padding: 20px; border-radius: 12px; box-shadow: 0 5px 15px rgba(0,0,0,.5); width: 90%; max-width: 500px; }
                .modal-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #dee2e6; padding-bottom: 15px; margin-bottom: 20px; }
                .modal-title { margin: 0; font-size: 1.5rem; font-weight: bold; color: #333; }
                .close-button { border: none; background: none; font-size: 1.75rem; font-weight: bold; cursor: pointer; padding: 0; line-height: 1; }
                .modal-body { margin-bottom: 20px; text-align: center; }
                .modal-body h6 { font-size: 1.1rem; color: #6c757d; margin-bottom: 1rem; }
                .modal-body p { font-size: 1.2rem; margin: 0.5rem 0; font-weight: 500; color: #212529; }
                .modal-footer { border-top: 1px solid #dee2e6; padding-top: 15px; text-align: right; }
                .btn-secondary { padding: 8px 16px; border-radius: 4px; border: 1px solid #ccc; cursor: pointer; background-color: #6c757d; color: white; }`}
    </style>
    </div>

   
  );
};


const DraftHeader: React.FC<{ users: LeagueParticipantDto[]; currentTurn: number | null; currentTurnUserCode: string | null; timerEndTime: number; isAutoPick: boolean; onAutoPickChange: (checked: boolean) => void; 
    }> = ({ users, currentTurn, currentTurnUserCode, timerEndTime, isAutoPick, onAutoPickChange }) =>  {
        
        
        if (!currentTurn){
            currentTurn = 0
        }
        
        let currentRound = 0
        let currentPick = 0

       
        if (currentTurn > 0){
            currentRound = Math.ceil(currentTurn / users.length)
            currentPick = currentTurn % users.length
            if (currentPick === 0){
                currentPick = users.length
            }
        }
        

        return(
        <header className="draft-header">
            <div className="timer-container">
                <div>TIMER</div>
                <DraftTimer endTime={timerEndTime} />
                <div className="timer-bar"></div>
            </div>
            <div className="round-container">
                <div>ROUND</div>   
                <div className="round-value">{currentRound}</div>
            </div>
            <div className="pick-container">
                <div>PICK</div>
                <div className="pick-value">{currentPick}</div>
            </div>
            
            <ul className="player-names">
                {users.map(user => (
                    <li key={user.user_code} className={user.user_code === currentTurnUserCode ? 'active-turn' : ''}>
                        <div>{user.name}</div>
                    </li>
                ))}
            </ul>
            <div className="auto-pick-container">
                <input
                    type="checkbox"
                    id="auto-pick-toggle"
                    checked={isAutoPick}
                    onChange={(e) => onAutoPickChange(e.target.checked)}
                />
                <label htmlFor="auto-pick-toggle">Auto-Pick</label>
            </div>
        </header>
    
);}





const PlayerList: React.FC<{ players: LeagueDraftPlayerResponse[]; onPick: (playerId: number) => void; disabled: boolean; }> = ({ players, onPick, disabled }) => {
    
    // 여기에 선수 검색, 포지션 필터링, 스탯 정렬 기능 추가 가능
    // 정렬 상태를 관리하는 State
    const [sortConfig, setSortConfig] = useState<{ key: keyof LeagueDraftPlayerResponse; direction: 'asc' | 'desc' } | null>({ key: 'avg', direction: 'desc' });
    const [searchTerm, setSearchTerm] = useState('')
    const [positionFilter, setPositionFilter] = useState('')
    const [selectedPlayer, setSelectedPlayer] = useState<LeagueDraftPlayerResponse | null>(null);

    // 정렬된 선수 목록을 계산하는 useMemo
    const filteredAndSortedPlayers = useMemo(() => {
        let sortablePlayers = [...players];

        // 1. 이름으로 검색
        if (searchTerm) {
            sortablePlayers = sortablePlayers.filter(player =>
                player.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
       
        if (positionFilter) {
            if (positionFilter === 'OF') {
                // 'OF'를 선택하면 좌익수(LF), 중견수(CF), 우익수(RF)를 모두 포함합니다.
                sortablePlayers = sortablePlayers.filter(player =>
                    ['LF', 'CF', 'RF'].includes(player.position)
                );
            } else {
                sortablePlayers = sortablePlayers.filter(player =>
                    player.position === positionFilter
                );
            }
            
           
        }

        if (sortConfig !== null) {
            sortablePlayers.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                } else if (typeof aValue === 'string' && typeof bValue === 'string') {
                    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortablePlayers;
    }, [players, sortConfig, searchTerm, positionFilter]);

    const handleReset = () => {
        setSearchTerm('')
        setPositionFilter('')
    }

    // 정렬 요청 핸들러
    const requestSort = (key: keyof Player) => {
        let direction: 'asc' | 'desc' = 'asc';
        // 현재 정렬 키와 동일한 키를 다시 클릭하면 방향을 바꿉니다.
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };


    // 정렬 방향을 시각적으로 표시하는 함수
    const getSortIndicator = (key: keyof Player) => {
        if (!sortConfig || sortConfig.key !== key) {
            return null;
        }
        return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    };

     const positionOptions = ['ALL', 'P', 'C', '1B', '2B', '3B', 'SS', 'OF'];
        const default_image = '/default-image.png'
     return (
        <div className="player-list-container">
            <div className="player-search-bar-container">
                <select className="player-position-filter"
                        value={positionFilter} 
                        onChange={(e) => setPositionFilter(e.target.value ==='ALL' ? '' : e.target.value)}>
                            {positionOptions.map(pos => <option key={pos} value={pos}>{pos}</option>)}
                </select>
                <button className="player-filter-reset"
                        onClick={handleReset}>
                    Reset
                </button>
                <input
                    type="text"
                    placeholder="선수 이름으로 검색..."
                    className="player-search-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
            {selectedPlayer && <PlayerModal player={selectedPlayer} onClose={() => setSelectedPlayer(null)} />}
            <table>
                <thead>
                    <tr>
                        <th>번호</th>
                        <th>선수</th>
                        <th onClick={() => requestSort('name')}>선수명{getSortIndicator('name')}</th>
                        <th onClick={() => requestSort('position')}>포지션{getSortIndicator('position')}</th>
                        <th onClick={() => requestSort('team')}>팀{getSortIndicator('team')}</th>
                        <th>선택</th>
                        <th onClick={() => requestSort('avg')}>평균 점수{getSortIndicator('avg')}</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredAndSortedPlayers.map((player, idx) => ( 
                        <tr key={player.id}>
                            <td>{idx+1}</td>
                            <td><img
                                    src={player.profile_image || default_image}
                                    alt={`${player.name} 선수 사진`}
                                    style={{
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        marginBottom: '15px'
                                    }}/> 
                            </td>
                            <td onClick={() => setSelectedPlayer(player)} style={{cursor: 'pointer', color: '#007bff'}}>
                                {player.name}
                            </td>
                            <td>{player.position}</td>
                            <td>{player.team}</td>
                            <td>
                                <button className="pick-button" onClick={() => onPick(player.id)} disabled={disabled}>
                                    Pick
                                </button>
                            </td>
                            <td>{player.avg.toFixed(2)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const MyTeam: React.FC<{ roster: RosterSlot[] }> = ({ roster }) => (
    <div className="my-team-container">
        <h4>My Team</h4>
        <table>
            <thead>
                <tr>
                    <th>포지션</th>
                    <th>선수명</th>
                </tr>
            </thead>
            <tbody>
                {roster.map((slot, index) => (
                    <tr key={index}>
                        <td>{slot.name}</td>
                        <td className="my-team-player-name">{slot.player ? slot.player.name : '—'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const DraftLog: React.FC<{ events: LeagueDraftEventLogDto[]; users: LeagueParticipantDto[]; allPlayers: LeagueDraftPlayerResponse[] }> = ({ events, users, allPlayers }) => {
    const userMap = useMemo(() => new Map(users.map(u => [u.user_code, u.name])), [users]);
    const playerMap = useMemo(() => new Map(allPlayers.map(p => [p.id, p.name])), [allPlayers]);
    const pickEvents = events.filter(e => e.type === 'pick');

    return (
        <div className="draft-log-container">
            <h4>Draft Log</h4>
            <ul>
                {pickEvents.slice().reverse().map(event => {
                    const [, playerId] = event.data.split(',').map(Number);
                    return (
                        <li key={event.id}>
                            {userMap.get(event.user_code) || 'Unknown'} 님이 {playerMap.get(playerId) || `Player #${playerId}`} 선수를 선택했습니다.
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};


// --- 드래프트 페이지 메인 컴포넌트 ---

interface DraftComponentProps {
    league: LeagueDetailDto;
}

const DraftComponent: React.FC<DraftComponentProps> = ({ league }) => {
    const { user, fetchApi } = useAuth();

    const [state, dispatch] = useReducer(draftReducer, {
        rule: {} as KBOLeagueDraftRuleDto,
        teamSlotList: [],
        availablePlayerList: [],
        currentTurn: 0,
        currentTurnUserCode: null,
        myUserCode: league.my_user_code,
        isMyTurn: false,
        isAutoPick: false,
        timerEndTime: 0,
        lastEventId: 0,
        isDraftFinished: false,
    });
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [partificipantList, setPartificipantList] = useState<LeagueParticipantDto[]>([]);
    const [allPlayerList, setAllPlayerList] = useState<LeagueDraftPlayerResponse[]>([]);
    const [lastEventId, setLastEventId] = useState<number>(0);
    const [draftLog, setDraftLog] = useState<LeagueDraftEventLogDto[]>([]);
    const [activeTab, setActiveTab] = useState<'draft' | 'myteam' | 'log'>('draft');
    const isMobile = useMediaQuery('(max-width: 768px)');

    const handlePickPlayer = useCallback(async (playerId: number) => {
        if (!state.isMyTurn) return;

        const playerToPick = state.availablePlayerList.find(p => p.id === playerId);
        if (!playerToPick) return;

        if (!window.confirm(`${playerToPick.name} (${playerToPick.team}) 선수를 선택하시겠습니까?`)) return;

        try {
            await fetchApi(`/api/league/session/${league.code}/pick`, {
                method: 'POST', 
                body: JSON.stringify({ player_id: playerId }),
            });
            CommonUtil.logEvent("draft_player", { player_id: playerId, league_type: league.league_type });
        } catch (error) {
            logger.error(`선수 선택 실패: ${error}`, {
                userId: user?.id,
                leagueCode: league.code,
                playerId,
            });
            alert("선수 선택에 실패했습니다.");
        }
    }, [state.isMyTurn, state.availablePlayerList, fetchApi, league.code, league.league_type, user?.id]);
    
    const handleAutoPickChange = useCallback(async (isChecked: boolean) => {
        console.log('handleAutoPickChange')
        dispatch({ type: 'SET_AUTO_PICK', payload: isChecked });
        try {
            await fetchApi(`/api/league/session/${league.code}/auto`, {
                method: 'POST', 
                body: JSON.stringify({ auto: isChecked ? 'on' : 'off' }),
            });
        } catch (error) {
            console.error("자동 선택 모드 변경 실패:", error);
        }
    }, [league.code, fetchApi]);

    useEffect(() => {
        if (!league.code) return;
        
        // 초기 데이터 세팅
        (async () => {
            let isSuccess = true;

            // 참가자 조회
            const participantRes: DataResponse<LeagueSessionParticipantRankResponse> = await fetchApi(`/api/league/session/${league.code}/rank`);
            if (participantRes.code === 0) {
                setPartificipantList(participantRes.data?.rankList ?? []);
            } else {
                alert("참가자 조회에 실패했습니다.");
                window.location.reload();
                return;
            }

            // 규칙 조회
            const ruleRes: DataResponse<KBOLeagueRuleResponse> = await fetchApi(`/api/league/session/${league.code}/rule`);
            if (ruleRes.code === 0) {
                setAllPlayerList(ruleRes.data?.player_list ?? []);
            } else {
                alert("규칙 조회에 실패했습니다.");
                window.location.reload();
                return;
            }

            dispatch({ type: 'INITIALIZE', payload: { 
                rule: ruleRes.data?.draft_rule as KBOLeagueDraftRuleDto,
                teamSlotList: (ruleRes.data?.position_list || []).map(pos => ({ ...pos, player: null })) as RosterSlot[],
                availablePlayerList: [...ruleRes.data?.player_list as LeagueDraftPlayerResponse[]],
                myUserCode: league.my_user_code,
                draftAt: league.draft_at
            } });
            setIsInitialized(isSuccess);
        })();
    }, [league.code, league.draft_at, league.my_user_code, fetchApi])

    useEffect(() => {
        if (!isInitialized) return;
        
        let isPollingActive = true;
        
        const poll = async () => {
            if (!isPollingActive) return;

            try {
                const response: LeagueDraftPollResponse = await fetchApi(`/api/league/session/${league.code}/poll?start=${lastEventId}`);                
                if (response.code === 0 && response.events.length > 0) {
                    setDraftLog(draftLog => [...draftLog, ...response.events]);

                    for (const event of response.events) {
                        if (lastEventId < event.id) {
                            setLastEventId(event.id)
                        }
                        
                        switch (event.type) {
                        case 'start':
                        {
                            const userCodeOrderList = event.data.split(',');
                            setPartificipantList(partificipantList => {
                                return partificipantList.sort((a, b) => {
                                    const aIndex = userCodeOrderList.indexOf(a.user_code);
                                    const bIndex = userCodeOrderList.indexOf(b.user_code);
                                    return aIndex - bIndex;
                                });
                            });
                            dispatch({ type: 'START', payload: { eventAt: event.event_at } });
                            break;
                        }

                        case 'turn':
                        {
                            const [turn, userCode] = event.data.split(',');
                            dispatch({ type: 'SET_TURN', payload: { 
                                turn: parseInt(turn), 
                                userCode, 
                                eventAt: event.event_at 
                            } });
                            break;
                        }

                        case 'pick':
                        {
                            const [, pickedPlayerId, positionIndex] = event.data.split(',').map(Number);
                            dispatch({ type: 'PLAYER_PICKED', payload: { 
                                pickedPlayerId, 
                                positionIndex, 
                                pickerUserCode: event.user_code,
                                eventAt: event.event_at 
                            } });
                            break;
                        }

                        case 'finish':
                        {
                            dispatch({ type: 'DRAFT_FINISH', payload: undefined });
                            isPollingActive = false;
                            alert("드래프트가 종료되었습니다!");
                            window.location.reload();
                            return;
                        }
                        }
                    }
                }
            } catch (error) {
                console.error("폴링 에러:", error);
            } finally {
                if(isPollingActive) {
                    setTimeout(poll, 1000);
                }
            }
        };

        poll();

        return () => { isPollingActive = false; };
    }, [isInitialized, league.code, fetchApi, lastEventId]); // lastEventId를 의존성에서 제거하여 폴링 루프가 스스로 관리하도록 함

    const renderActiveTabContent = () => {
        switch (activeTab) {
            case 'myteam': return <MyTeam roster={state.teamSlotList} />;
            case 'log': return <DraftLog events={draftLog} users={partificipantList} allPlayers={allPlayerList} />;
            case 'draft':
            default: return <PlayerList players={state.availablePlayerList} onPick={handlePickPlayer} disabled={!state.isMyTurn} />;
        }
    };

    if (state.isDraftFinished) {
        return <div>드래프트가 종료되었습니다. 라인업 페이지로 이동합니다...</div>;
    }

    return (
        <div className="draft-page">
            <DraftHeader
                users={partificipantList}  currentTurn={state.currentTurn} currentTurnUserCode={state.currentTurnUserCode}
                timerEndTime={state.timerEndTime} isAutoPick={state.isAutoPick}
                onAutoPickChange={handleAutoPickChange}
            />
            
            {
                isMobile ? (
                    <>
                        <nav className="draft-tabs ">
                            <button className={`tab-button ${activeTab === 'draft' ? 'active' : ''}`} onClick={() => setActiveTab('draft')}>드래프트</button>
                            <button className={`tab-button ${activeTab === 'myteam' ? 'active' : ''}`} onClick={() => setActiveTab('myteam')}>내 팀</button>
                            <button className={`tab-button ${activeTab === 'log' ? 'active' : ''}`} onClick={() => setActiveTab('log')}>드래프트 기록</button>
                        </nav>
                        <div className="draft-content-area">
                            {renderActiveTabContent()}
                        </div>
                    </>) 
                    : 
                    (
                        <div className="draft-container-desktop">
                            <main className="draft-main-content">
                                <PlayerList players={state.availablePlayerList} onPick={handlePickPlayer} disabled={!state.isMyTurn} />
                            </main>
                            <aside className="draft-sidebar">
                                <MyTeam roster={state.teamSlotList} />
                                <DraftLog events={draftLog} users={partificipantList} allPlayers={allPlayerList} />
                            </aside>
                        </div>
                        )
            }
            
        

            <style>{`
                .draft-page { display: flex; flex-direction: column; height: 100vh; font-family: sans-serif; background-color: #f8f9fa; }
                .draft-header { height: 80px; background-color:#0a272f; color: white; display: flex; align-items: center; padding: 0 10px; flex-shrink: 0; }

                .timer-container { width: 80px; text-align: center; }
                .round-container, .pick-container {display: block; width: 60px; text-align: center;}
                .round-value, .pick-value {height: 43px; color: #0c7bb3; font-weight: bold; font-size: 24px;}
                .timer-value { font-size: 24px; color: #0c7bb3; font-weight: bold; }
                .timer-bar { background-color: #6b7e82; height: 2px; width: 80px; border-radius: 10px; margin: 5px auto 0; }
                .player-search-bar-container {position: sticky; top: 0px; z-index: 2; display: flex; align-items: center; height; 10px; background-color: #f8f9fa;}
                .player-search-input {padding: 5px; margin: 5px; margin-left:auto;}
                .player-position-filter {padding: 5px; margin:5px; border-radius: 30px;}
                .player-filter-reset {background-color:rgb(255, 255, 255); padding: 5px; padding-left: 10px; padding-right: 10px; margin:5px; border-radius: 30px; height: 32.5 px; border-width: 1px; margin-right: auto;}
                .player-names { display: flex; flex: 1; list-style: none; padding: 0; margin: 0; overflow-x: auto; justify-content: space-around; align-items: center; }
                .player-names li { flex-shrink: 0; text-align: center; background-color: #0c7bb3; padding: 0 15px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .player-names li.active-turn { font-weight: bold; color: #ffeb3b; transform: scale(1.1); transition: all 0.3s ease-in-out; }
                .auto-pick-container { display: flex; align-items: center; gap: 5px; margin-left: auto; }

                .draft-container-desktop { display: flex; flex: 1; overflow: hidden; }
                .draft-main-content { flex: 1; overflow-y: auto; }
                .draft-sidebar { width: 280px; border-left: 1px solid #dee2e6; display: flex; flex-direction: column; }
                .my-team-container, .draft-log-container { flex: 1; overflow-y: auto; padding: 10px; border-top: 1px solid #eee; }
                .my-team-container:first-child { border-top: none; }
                .my-team-container h4, .draft-log-container h4 { position: sticky; top: 0px; height: 47px; margin-top: 8 px; margin-bottom: 0px; padding: 5px; background-color: #f8f9fa }

                .draft-content-area { flex: 1; overflow-y: auto; display: flex; flex-direction: column; }
                .draft-log-container ul { list-style: none; padding: 0; margin: 0; font-size: 14px; }
                .draft-log-container li { padding: 4px 0; border-bottom: 1px solid #f2f2f2; }
                table { width: 100%; border-collapse: collapse; }
                thead { position: sticky; top: 46px; background-color: #f8f9fa; z-index: 1; }
                th, td { padding: 8px; border-bottom: 1px solid #eee; text-align: center; font-size: 14px; }
                .my-team-player-name { text-align: left; }
                .pick-button { background-color: #007bff; color: white; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer; }
                .pick-button:disabled { background-color: #ccc; cursor: not-allowed; 

                .draft-tabs { display: flex; background-color: #e9ecef; border-bottom: 1px solid #dee2e6; flex-shrink: 0; }
                .tab-button { flex: 1; padding: 12px 15px; border: none; background-color: transparent; cursor: pointer; font-size: 16px; font-weight: 500; color: #495057; border-bottom: 3px solid transparent; transition: all 0.2s ease-in-out; text-align: center; }
                .tab-button:hover { background-color: #dee2e6; }
                .tab-button.active { color: #007bff; border-bottom-color: #007bff; font-weight: bold; }
                .tab-content-container { padding: 15px; height: 100%; overflow-y: auto; }

                .player-list-container { display: flex; overflow-y: auto; flex-direction: column; height: 100%; padding: 0; }
                .player-search-bar-container { position: sticky; top: 0; z-index: 10; display: flex; align-items: center; gap: 10px; padding: 8px; background-color: #f8f9fa; border-bottom: 1px solid #dee2e6; }
                .player-list-table-wrapper { flex-grow: 1; overflow-y: auto; }
                `}</style>
        </div>
    );
};

export default DraftComponent;
